import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, MessageCircle, MessageSquareOff, Trash2, ShieldCheck,
} from "lucide-react";
import { useModerationQueue } from "../../lib/adminQueues";
import { useSelection } from "../../lib/useSelection";
import { SelectCheckbox } from "./SelectCheckbox";
import { BulkActionBar } from "./BulkActionBar";
import { BulkConfirmDialog } from "./BulkConfirmDialog";
import { SummaryList } from "./CampaignReviewTab";
import { WallAlertSettings } from "./WallAlertSettings";
import { RecentWallPosts } from "./RecentWallPosts";

export const ModerationTab = () => {
  const { items, loading, error, refresh, bulkDismiss, bulkRemove } = useModerationQueue();
  const sel = useSelection();
  const [action, setAction] = useState(null); // null | "dismiss" | "remove"
  const [showSettings, setShowSettings] = useState(false);

  const ids = useMemo(() => items.map((m) => m.id), [items]);
  const selectedItems = items.filter((m) => sel.isSelected(m.id));

  const runDismiss = async () => {
    const res = await bulkDismiss(sel.ids);
    const p = res.processed || 0;
    toast.success(`${p} flag${p === 1 ? "" : "s"} cleared.`, {
      description: "Messages stay live on the supporter wall.",
    });
    sel.clear();
    setAction(null);
  };

  const runRemove = async () => {
    const res = await bulkRemove(sel.ids);
    const p = res.processed || 0;
    toast.success(`${p} message${p === 1 ? "" : "s"} removed from the wall.`, {
      description: "Authors won't be notified.",
    });
    sel.clear();
    setAction(null);
  };

  return (
    <div data-testid="tab-moderation" className="space-y-10">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif text-2xl leading-tight text-ink">Reported posts</h3>
        <button
          type="button"
          onClick={() => setShowSettings((v) => !v)}
          data-testid="moderation-toggle-settings"
          aria-expanded={showSettings}
          className="text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink"
        >
          {showSettings ? "Hide alert settings" : "Alert settings"}
        </button>
      </div>
      {showSettings && <WallAlertSettings />}

      <div>
      {error && (
        <p className="mb-6 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">{error}</p>
      )}

      {items.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-border bg-bone px-5 py-3">
          <label className="flex items-center gap-3 text-sm font-medium text-ink">
            <SelectCheckbox
              testid="moderation-select-all"
              checked={sel.allSelected(ids)}
              indeterminate={sel.count > 0 && !sel.allSelected(ids)}
              onChange={(v) => (v ? sel.selectAll(ids) : sel.clear())}
              label="Select all flagged messages"
            />
            {sel.count === 0
              ? `Select all (${items.length})`
              : `${sel.count} of ${items.length} selected`}
          </label>
          <button
            type="button"
            onClick={refresh}
            data-testid="moderation-refresh"
            className="text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink"
          >
            Refresh
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-olive">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading flagged messages…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="moderation-empty"
          className="rounded-2xl border-2 border-dashed border-border bg-bone p-10 text-center"
        >
          <ShieldCheck className="mx-auto h-9 w-9 text-olive" strokeWidth={1.5} />
          <h3 className="mt-3 font-serif text-2xl text-ink">No reports waiting.</h3>
          <p className="mt-1 text-sm text-olive">
            No flagged supporter messages waiting on your call. Every recent post is listed below.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {items.map((m) => (
              <MessageRow
                key={m.id}
                m={m}
                checked={sel.isSelected(m.id)}
                onToggle={() => sel.toggle(m.id)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
      </div>

      <RecentWallPosts key={items.length} />

      <BulkActionBar count={sel.count} label="message" onClear={sel.clear}>
        <button
          type="button"
          onClick={() => setAction("dismiss")}
          data-testid="moderation-bulk-dismiss"
          className="inline-flex items-center gap-1.5 rounded-full bg-yellow px-4 py-2 text-xs font-semibold text-ink transition-colors hover:brightness-95"
        >
          <MessageSquareOff className="h-3.5 w-3.5" strokeWidth={2} />
          Dismiss flag
        </button>
        <button
          type="button"
          onClick={() => setAction("remove")}
          data-testid="moderation-bulk-remove"
          className="inline-flex items-center gap-1.5 rounded-full border border-sand/25 bg-sand/5 px-4 py-2 text-xs font-semibold text-sand transition-colors hover:bg-sand/10"
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
          Remove
        </button>
      </BulkActionBar>

      <BulkConfirmDialog
        open={action === "dismiss"}
        onOpenChange={(v) => !v && setAction(null)}
        title={<>Dismiss {sel.count} flag{sel.count === 1 ? "" : "s"}?</>}
        description="Messages stay live on the supporter wall. Authors are not notified."
        itemsSummary={<SummaryList items={selectedItems.map((m) => ({
          left: m.author_name,
          right: m.flag_reason,
        }))} />}
        confirmLabel="Dismiss and clear flag"
        onConfirm={runDismiss}
      />
      <BulkConfirmDialog
        open={action === "remove"}
        onOpenChange={(v) => !v && setAction(null)}
        title={<>Remove {sel.count} message{sel.count === 1 ? "" : "s"} from the wall?</>}
        description="This soft-deletes the message. Authors are not notified."
        itemsSummary={<SummaryList items={selectedItems.map((m) => ({
          left: m.author_name,
          right: m.campaign_title,
        }))} />}
        confirmLabel="Remove from wall"
        confirmTone="danger"
        onConfirm={runRemove}
      />
    </div>
  );
};

const MessageRow = ({ m, checked, onToggle }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6, height: 0 }}
    transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
    className={`flex items-start gap-4 rounded-xl border-2 p-5 transition-colors ${
      checked ? "border-ink bg-yellow/5" : "border-ink/10 bg-bone hover:border-ink/25"
    }`}
    data-testid={`moderation-row-${m.id}`}
  >
    <div className="pt-0.5">
      <SelectCheckbox
        checked={checked}
        onChange={onToggle}
        testid={`moderation-checkbox-${m.id}`}
        label={`Select message from ${m.author_name}`}
      />
    </div>

    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-scarlet/12 text-scarlet">
      <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
    </div>

    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="truncate font-serif text-lg text-ink">
          <span className="font-semibold">{m.author_name}</span>{" "}
          <span className="text-olive">on</span>{" "}
          <span className="italic">{m.campaign_title}</span>
        </p>
        <span className="text-[10px] uppercase tracking-[0.22em] text-scarlet">
          {m.flag_reason}
        </span>
      </div>
      <blockquote
        data-testid={`moderation-message-${m.id}`}
        className="mt-2 rounded-md border-l-4 border-scarlet bg-scarlet/[0.04] px-4 py-2 text-sm italic text-ink"
      >
        &ldquo;{m.message}&rdquo;
      </blockquote>
    </div>
  </motion.article>
);
